/* eslint-disable new-cap */
/* eslint-disable func-names */
import { BlueprintInstanceType } from '../shared.enums';
import { KitchenDesignBase } from '../kitchen-design-base';
import { Publish } from '../../decorators';
import { surroundMaterialData } from './surround-material.data';

import { CornerAssetMap, SurroundAsset, SurroundAssetMap } from '../public.types';
import { SURROUND_TEXTURE_DIMENSION } from './constants';
import { getDefaultThickness, getThicknessForRole, hasPerRoleThickness, type PanelRole } from './functions/get-thickness-for-role';
import { SurroundMaterialData, SurroundMaterialDataIn, SurroundMaterialOptions } from './types';

import cornerData from '../data/corner-asset.map.json';
import surroundData from '../data/surround-asset.map.json';

/**
 * A class for handling Surround material selection and persistence
*/
export class SurroundMaterial extends KitchenDesignBase<SurroundMaterialDataIn> {
	/** Maps to a key in the countertop data file */
	#key: string;
	/** A friendly name for the selected countertop. */
	#displayName: string;
	/** sku code for material */
	#code: string;
	/** scale for the corner texture */
	#cornerScale: number;
	/** name of the corner texture file */
	#cornerTexture: string;
	/** name of the texture file */
	#texture: string;
	/** is the surround active */
	#enabled: boolean;
	/** scaling the texture for realistic viewing */
	#scale: { height: number, width: number };
	//* min overhang
	#minOverhang: number;
	/** What type of surround; i.e wood, stacked stone, etc.. */
	#material: string;
	/** the material type prior to the most recent setMaterial() call; null on first init */
	#previousMaterial: string | null = null;
	/** permits surround data to be overridden (different than parent) */
	#override: boolean;

	/**
	 * The constructor of the SurroundMaterial class.
	 *
	 * @param options is of generic type ClassOptions for SurroundMaterialData with a specified ParentType
	 */
	constructor (options: SurroundMaterialOptions = {}) {
		super(BlueprintInstanceType.SurroundMaterial, options);

		const data = surroundMaterialData(options.dataIn);
		this.#override = data.override;
		this.#key = options.syncTo && !this.#override ? options.syncTo.#key : data.key;
		this.setMaterial(this.#key);

		this.setInitialized()
	}

	get key (): string { return this.#key }
	get code (): string { return this.#code }
	get cornerScale (): number { return this.#cornerScale }
	get cornerTexture (): string { return this.#cornerTexture }
	get displayName (): string { return this.#displayName }
	get texture (): string { return this.#texture }
	get enabled (): boolean { return this.#enabled }
	get scale (): Record<string, number> { return { ...this.#scale } }
	get minOverhang (): number { return this.#minOverhang }
	get material (): string { return this.#material }
	/** Material type before the most recent setMaterial() call — null before the first switch. Consumers use this to detect material-family transitions (e.g. standard → MOK). */
	get previousMaterial (): string | null { return this.#previousMaterial }
	/** Returns the default (front) thickness for backward compatibility */
	get thickness (): number { return getDefaultThickness(this.#material) }
	/** Returns true if this material has different thicknesses per panel role */
	get hasPerRoleThickness (): boolean { return hasPerRoleThickness(this.#material) }

	get override ():boolean { return this.#override }
	set override (v: boolean) {	this.#override = v }

	/**
	 * Returns the thickness for a specific panel role (front, back, left, right).
	 * For materials with uniform thickness, all roles return the same value.
	 * @param role the panel role to get thickness for
	 */
	public getThicknessForRole (role: PanelRole): number {
		return getThicknessForRole(this.#material, role);
	}

	/**
	 * A public method. Called to select a new surround from the datafile.
	 */
	@Publish('surround_material', 'updated')
	public setMaterial (key: string): boolean {
		// if there is a kitchen parent then override must be set to change
		if (!this.initialized || this.override) {
			const surroundMap: SurroundAssetMap = surroundData;
			const cornerMap: CornerAssetMap = cornerData;

			const surround = surroundMap.data[key] ?? null;
			const corner = cornerMap[key] ?? null;
			this.#key = key;
			this.#code = surround?.code ?? '^^';
			this.#displayName = surround?.displayName ?? '';
			this.#texture = surround?.texture ?? '';
			this.#cornerTexture = corner?.texture ?? '';
			this.#cornerScale = corner?.scale ?? 1;
			this.#enabled = surround?.enabled ?? false;
			this.#scale = {
				...surround?.scale ?? {},
				width: (surround?.scale.width ?? 0) * SURROUND_TEXTURE_DIMENSION,
				height: (surround?.scale.height ?? 0) * SURROUND_TEXTURE_DIMENSION
			};
			this.#minOverhang = surround?.minOverhang ?? 0;
			this.#previousMaterial = this.#material ?? null;
			this.#material = surround?.material ?? '';
			return surround !== null;
		}

		return false;
	}

	/**
	 * a Public method. Used to create an object of SurroundMaterialData for persisting the class.
	 */
	public serialize (): SurroundMaterialData {
		const record: SurroundMaterialData = {
			key: this.#key,
			type: this.#material
		};

		if (this.#override) record.override = true;

		return record;
	}

	static assetData: Record<string, SurroundAsset> = (surroundData as unknown as SurroundAssetMap).data;

	/**
	 * Returns all enabled surround material options for programmatic or AI access.
	 */
	static getOptions (): SurroundMaterialOptionsResult {
		const options: SurroundMaterialOptionItem[] = [];
		const assetMap = surroundData as unknown as SurroundAssetMap;

		for (const [key, asset] of Object.entries(assetMap.data)) {
			if (asset.enabled && asset.texture) {
				options.push({
					key,
					displayName: asset.displayName,
					material: asset.material,
					code: asset.code,
					thumbnailUrl: asset.texture
				});
			}
		}

		return {
			options,
			defaultKey: assetMap.default
		};
	}
}

export interface SurroundMaterialOptionItem {
	key: string;
	displayName: string;
	material: string;
	code: string;
	thumbnailUrl: string;
}

export interface SurroundMaterialOptionsResult {
	options: SurroundMaterialOptionItem[];
	defaultKey: string;
}
